/**
 * SettingsModal — the unified account settings surface (claude.ai parity), on
 * the aikka square DA (os.css tokens, 5px radii; the `check:os-da` gate is
 * load-bearing).
 *
 * One modal, a left nav split into two groups — **Paramètres** (Général, Compte,
 * Capacités, Confidentialité, Sécurité, Facturation, Utilisation) and
 * **Intégrations** (Connecteurs) — and a scrollable content panel. It *replaces*
 * the `/dashboard/settings` route (now retired to a redirect): the avatar menu
 * "Paramètres" opens THIS modal, with no route navigation anywhere in the flow.
 * Facturation is self-contained too (the Stripe customer portal, no
 * `/dashboard/billing` hop).
 *
 * NAMING (arbitrated debt): the second group used to be called **Personnaliser**
 * — the same name the sidebar entry carries for `CustomizePanel`, a DIFFERENT
 * modal holding the Space and the agent instructions. Two surfaces under one
 * name, with `CustomInstructionsFields` rendered in both, meant an operator could
 * hold two live drafts of the same server preference. Resolved in one direction:
 * **`CustomizePanel` is the only "Personnaliser" in the product** (one click from
 * the rail — it is the counterpart of the composer's removed context bar, so it
 * must stay fast to reach). This modal's second group is now *Intégrations*, and
 * Général no longer renders the instructions block: it links across instead
 * (`onOpenCustomize`), mirroring the Personnaliser → Connecteurs bridge that
 * already went the other way.
 *
 * Every panel is a REUSED section component wired to its real endpoint where one
 * exists (name → PATCH /auth/me, drafting model → /agent/preferences, billing →
 * the tier/usage queries, connectors → the real `/v1/connectors` routes via
 * `httpConnectorsApi`). Only the active panel mounts, so a section's queries fire
 * only when it is visited.
 *
 * Accessibility: `role="dialog"` + `aria-modal`, Escape to close, click-outside
 * to close, a Tab focus-trap, and roving ↑/↓ focus across the left nav.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Brain,
  KeyRound,
  Lock,
  Plug,
  ShieldCheck,
  SlidersHorizontal,
  UserCog,
  Wallet,
  X,
} from 'lucide-react';
import { SettingsGeneralSection } from '@/components/settings/settings-general-section';
import { SettingsAccountSection } from '@/components/settings/settings-account-section';
import { SettingsMemorySection } from '@/components/settings/settings-memory-section';
import { SettingsPrivacySection } from '@/components/settings/settings-privacy-section';
import { SettingsSecuritySection } from '@/components/settings/settings-security-section';
import { SettingsBillingSection } from '@/components/settings/settings-billing-section';
import { SettingsIntegrationsSection } from '@/components/settings/settings-integrations-section';
import { SettingsApiKeysSection } from '@/components/settings/settings-api-keys-section';
import { ConnectorsSection } from '@os/components/ConnectorsSection';
import { httpConnectorsApi } from '@os/lib/connectorsHttp';
import { CONNECTORS_ENABLED } from '@/lib/featureFlags';

/** The nav item ids — also the initial-section contract with the shell. */
export type SettingsSectionId =
  | 'general'
  | 'account'
  | 'memory'
  | 'privacy'
  | 'security'
  | 'billing'
  | 'apiKeys'
  | 'integrations'
  | 'connectors';

type Group = 'settings' | 'integrations';

interface SectionDef {
  id: SettingsSectionId;
  group: Group;
  icon: React.ReactNode;
}

const SECTIONS: SectionDef[] = [
  { id: 'general', group: 'settings', icon: <SlidersHorizontal className="h-4 w-4" strokeWidth={1.7} /> },
  { id: 'account', group: 'settings', icon: <UserCog className="h-4 w-4" strokeWidth={1.7} /> },
  // Mémoire — ex-« Capacités ». Le panneau ne porte plus que la mémoire des
  // conversations : le sélecteur de modèle et le bloc Général (mode d'accès aux
  // outils, recherche connecteurs) ont été retirés, donc le nom « Capacités »
  // ne décrivait plus rien de ce qui reste.
  { id: 'memory', group: 'settings', icon: <Brain className="h-4 w-4" strokeWidth={1.7} /> },
  { id: 'privacy', group: 'settings', icon: <ShieldCheck className="h-4 w-4" strokeWidth={1.7} /> },
  { id: 'security', group: 'settings', icon: <Lock className="h-4 w-4" strokeWidth={1.7} /> },
  { id: 'billing', group: 'settings', icon: <Wallet className="h-4 w-4" strokeWidth={1.7} /> },
  // Utilisation : retirée. Elle doublait Facturation sans rien apporter que
  // Facturation ne dise déjà (Notion, « Usage : delete »).
  // Clés API — and it IS exposed, unlike Connecteurs above, because it is
  // finished: every control is wired to a real endpoint (`POST/GET/DELETE
  // /v1/keys`), and the surface those keys authenticate (`/v1/public/*`) is
  // live and billed. The rule the connectors entry follows is not "hide new
  // things", it is "never navigate to a screen that can only say bientôt
  // disponible"; this one can do the whole job.
  { id: 'apiKeys', group: 'settings', icon: <KeyRound className="h-4 w-4" strokeWidth={1.7} /> },
  // Connecteurs : masqué tant que `VITE_CONNECTORS_ENABLED` n'est pas posé.
  // Aucun fournisseur n'est provisionné, donc la surface ne peut que répondre
  // « bientôt disponible » — ce qui se lit comme une fonctionnalité cassée
  // plutôt qu'absente. Rien n'est supprimé : le flag la fait revenir telle
  // quelle. Voir `lib/featureFlags.ts`.
  // Intégrations — TOUJOURS visible, et volontairement minuscule : un texte et un
  // bouton qui prévient l'équipe. C'est la contrepartie honnête de « Connecteurs »
  // ci-dessous, masqué tant qu'aucun fournisseur n'est provisionné : plutôt que de
  // ne rien offrir du tout, on offre la seule action qui existe réellement.
  { id: 'integrations', group: 'integrations', icon: <Plug className="h-4 w-4" strokeWidth={1.7} /> },
  ...(CONNECTORS_ENABLED
    ? [{ id: 'connectors' as const, group: 'integrations' as const, icon: <Plug className="h-4 w-4" strokeWidth={1.7} /> }]
    : []),
];

const GROUP_ORDER: Group[] = ['settings', 'integrations'];

export interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
  /** Which section to land on when opened (defaults to "general"). */
  initialSection?: SettingsSectionId;
  /** Re-run the first-run intro from Général → Profil ("Revoir l'introduction"). */
  onReplayOnboarding?: () => void;
  /**
   * Ouvre la modale de recharge depuis Facturation → « Acheter des crédits ».
   *
   * Elle appartient au shell, pas à ce panneau : le bouton lançait un Checkout
   * Stripe en dur à 39,99 $, et le client l'a renvoyé sur la pop-up. Passer par
   * une prop plutôt que par `?topup=1` permet de FERMER ce panneau avant
   * d'ouvrir l'autre — sinon deux modales s'empilent.
   */
  onTopUp?: () => void;
}

function focusableWithin(root: HTMLElement): HTMLElement[] {
  return Array.from(
    root.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((el) => el.offsetParent !== null || el === document.activeElement);
}

export function SettingsModal({
  open,
  onClose,
  initialSection = 'general',
  onReplayOnboarding,
  onTopUp,
}: SettingsModalProps) {
  const { t } = useTranslation('os');
  const [active, setActive] = useState<SettingsSectionId>(initialSection);
  const dialogRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // Land on the requested section each time the modal is opened.
  useEffect(() => {
    if (open) setActive(initialSection);
  }, [open, initialSection]);

  // Focus management: remember what had focus, move focus into the dialog, and
  // restore it on close (parity + a11y).
  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    // Focus the active nav item once mounted.
    const id = window.setTimeout(() => {
      navRef.current
        ?.querySelector<HTMLElement>(`[data-testid="settings-nav-${active}"]`)
        ?.focus();
    }, 0);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open) return;
    previouslyFocused.current?.focus?.();
  }, [open]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'Tab' && dialogRef.current) {
        // Focus-trap: keep Tab inside the dialog.
        const items = focusableWithin(dialogRef.current);
        if (items.length === 0) return;
        const first = items[0]!;
        const last = items[items.length - 1]!;
        const activeEl = document.activeElement as HTMLElement;
        if (e.shiftKey && activeEl === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && activeEl === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [onClose],
  );

  // Roving ↑/↓ across the left nav.
  const onNavKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const buttons = Array.from(
      navRef.current?.querySelectorAll<HTMLElement>('[data-testid^="settings-nav-"]') ?? [],
    );
    const current = buttons.indexOf(document.activeElement as HTMLElement);
    const delta = e.key === 'ArrowDown' ? 1 : -1;
    const next = ((current + delta) % buttons.length + buttons.length) % buttons.length;
    buttons[next]?.focus();
  };

  // Empty groups are dropped, header included. Connecteurs is the only member of
  // « Intégrations » and it is flag-gated off, so the group rendered nothing but
  // its own label — a heading over a void (Notion: « Retirer le sous-titre
  // integration »). Filtering here rather than deleting the label keeps the
  // group intact for the day `VITE_CONNECTORS_ENABLED` is set.
  const grouped = useMemo(() => {
    return GROUP_ORDER.map((group) => ({
      group,
      items: SECTIONS.filter((s) => s.group === group),
    })).filter(({ items }) => items.length > 0);
  }, []);

  if (!open) return null;

  const panels: Record<SettingsSectionId, React.ReactNode> = {
    general: <SettingsGeneralSection onReplayOnboarding={onReplayOnboarding} />,
    account: <SettingsAccountSection />,
    memory: <SettingsMemorySection />,
    privacy: <SettingsPrivacySection />,
    security: <SettingsSecuritySection />,
    billing: <SettingsBillingSection onTopUp={onTopUp} />,
    integrations: <SettingsIntegrationsSection />,
    apiKeys: <SettingsApiKeysSection />,
    // Le corps reste défini même masqué : la nav ne propose plus l'entrée, donc
    // elle est inatteignable, et le type Record reste total (pas d'entrée
    // optionnelle qui obligerait chaque lecteur à gérer un undefined).
    connectors: CONNECTORS_ENABLED ? <ConnectorsSection api={httpConnectorsApi} /> : null,
  };

  return createPortal(
    // Portalled to `document.body`, this surface escapes the shell's `.os-scope`
    // subtree — so the aikka tokens (--signal / --card / --surface-* / …) and the
    // brand focus outline, both scoped under `.os-scope` in os.css, would never
    // reach it. Re-establish the scope inside the portal so the now-sole settings
    // surface keeps the square brand DA and draws the ember focus ring rather than
    // the browser blue. The wrapper collapses to a 0px box (its only child is
    // `position: fixed`, out of flow), so it never paints a field of its own; the
    // custom-property cascade and the `.os-scope :where(…)` focus rules still
    // reach every descendant.
    <div
      className="os-scope"
      // Portail monté sur `document.body` : hors de l'arbre des coques,
      // donc le masque Contentsquare ne descend pas jusqu'ici. Il lui en
      // faut un à lui. Voir `layout/ContentsquareProvider.tsx`.
      data-cs-mask
    >
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('settings.title')}
      data-testid="settings-modal"
      onKeyDown={onKeyDown}
    >
      <button
        type="button"
        aria-label={t('settings.close')}
        data-testid="settings-modal-backdrop"
        className="os-modal-backdrop absolute inset-0 bg-scrim/40"
        onClick={onClose}
      />
      <div
        ref={dialogRef}
        className="os-modal-panel glass relative z-10 flex h-[85vh] max-h-[780px] w-full max-w-4xl flex-col overflow-hidden border border-border sm:flex-row"
      >
        {/* Left nav */}
        <div
          ref={navRef}
          onKeyDown={onNavKeyDown}
          className="shrink-0 space-y-3 overflow-y-auto border-b border-border bg-surface-1 p-3 sm:w-[200px] sm:border-b-0 sm:border-r"
        >
          <div className="flex items-center justify-between sm:hidden">
            <h2 className="font-display text-[1rem] text-foreground">
              {t('settings.title')}
            </h2>
          </div>
          {grouped.map(({ group, items }) => (
            <div key={group}>
              <div
                data-testid={`settings-group-${group}`}
                className="px-2 pb-1 font-data text-[9px] uppercase tracking-[0.2em] text-ink-2"
              >
                {t(`settings.groups.${group}`)}
              </div>
              <div className="space-y-0.5">
                {items.map((s) => {
                  const isActive = s.id === active;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      data-testid={`settings-nav-${s.id}`}
                      aria-current={isActive ? 'page' : undefined}
                      onClick={() => setActive(s.id)}
                      className={`flex w-full items-center gap-2.5 rounded-control px-2.5 py-1.5 text-left text-[0.85rem] outline-none transition-colors focus-visible:ring-1 focus-visible:ring-state/60 ${
                        isActive
                          ? 'bg-secondary font-medium text-foreground'
                          : 'text-ink-2 hover:bg-secondary/60 hover:text-foreground'
                      }`}
                    >
                      <span className="flex h-4 w-4 shrink-0 items-center justify-center text-ink-2">
                        {s.icon}
                      </span>
                      <span className="truncate">{t(`settings.nav.${s.id}`)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
            <h2 className="truncate font-display text-[1.05rem] tracking-[-0.01em] text-foreground">
              {t(`settings.nav.${active}`)}
            </h2>
            <button
              type="button"
              aria-label={t('settings.close')}
              data-testid="settings-modal-close"
              onClick={onClose}
              className="os-icon-btn rounded-control p-1 text-ink-2 hover:bg-secondary hover:text-foreground"
            >
              <X className="h-4 w-4" strokeWidth={1.8} />
            </button>
          </div>
          <div
            key={active}
            data-testid={`settings-panel-${active}`}
            className="os-enter min-h-0 flex-1 overflow-y-auto px-5 py-5"
          >
            {panels[active]}
          </div>
        </div>
      </div>
    </div>
    </div>,
    document.body,
  );
}
